/*
 * Sprint V29 (Fr 27.02.2026) — Admin Landing-Analytics.
 * Route: /admin/landing-analytics (nur mit Admin-Login sichtbar).
 * Zeigt die 4 Hook-Varianten (a/b/c/d) mit den Conversion-Metriken
 * hero_view → cta_click → test_start → test_complete und der Overall-CR
 * pro Variante. Beste Variante wird visuell hervorgehoben.
 *
 * Auth-Model: Cookie-basiert via `_get_admin_user` (analog zu
 * AdminFeedbackOverview). Kein Manual-Token-Handling im Browser noetig.
 */
import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, TrendingUp, Trophy } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { TestExcludedNote } from "@/components/TestExcludedNote";
import { HEADLINE_CANDIDATES } from "@/data/landingHookCopy";

function fmtPercent(x) {
  if (typeof x !== "number") return "—";
  return `${(x * 100).toFixed(1)}%`;
}

function pctBarColor(rate) {
  if (rate >= 0.15) return "bg-emerald-500";
  if (rate >= 0.08) return "bg-amber-500";
  if (rate >= 0.03) return "bg-orange-500";
  return "bg-rose-500";
}

const VARIANT_LABELS = {
  a: "Hook A · Situation",
  b: "Hook B · Fokus",
  c: "Hook C · Andere",
  d: "Hook D · Wirkung",
  ...Object.fromEntries(
    Object.entries(HEADLINE_CANDIDATES).map(([id, c]) => [id, `${id} · ${c.headline.join(" ")} (Kandidat)`]),
  ),
};

export default function AdminLandingAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/landing-analytics");
      setData(r.data);
      setLastUpdate(new Date());
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setError("Diese Seite ist nur für Admins. Bitte als Admin einloggen.");
      } else {
        setError("Analytics konnten nicht geladen werden.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000); // Auto-Refresh alle 60 Sek
    return () => clearInterval(iv);
  }, []);

  const variants = data?.variants || [];

  // Best-Variant-Highlighting: hoechste Conversion-Rate mit mind. 20 hero_views
  // (Signifikanz-Schwelle — sonst geben Zufalls-Spitzen aus einem Klick eine
  // 100%-CR und verzerren die Anzeige komplett).
  const bestVariant = useMemo(() => {
    const candidates = variants.filter((v) => (v.hero_view || 0) >= 20);
    if (!candidates.length) return null;
    return candidates.reduce((a, b) => (a.conversion_rate > b.conversion_rate ? a : b));
  }, [variants]);

  const totalViews = variants.reduce((s, v) => s + (v.hero_view || 0), 0);
  const totalCompletes = variants.reduce((s, v) => s + (v.test_complete || 0), 0);
  const overallCR = totalViews > 0 ? totalCompletes / totalViews : 0;

  return (
    <AdminLayout>
      <div data-testid="admin-landing-analytics-page">
        <header className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">
              Admin · A/B-Test
            </p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white" data-testid="admin-landing-analytics-headline">
              Landing-Page Conversion
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Vergleich der 4 Hook-Varianten · Auto-Refresh alle 60 Sek
              {lastUpdate && (
                <span className="ml-2 text-slate-400">
                  · Zuletzt: {lastUpdate.toLocaleTimeString("de-DE")}
                </span>
              )}
            </p>
            <TestExcludedNote count={data?.test_excluded} testId="landing-analytics-test-excluded" />
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="admin-landing-analytics-refresh"
            className="inline-flex items-center gap-2 rounded-lg bg-[#4563BB] hover:opacity-90 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </button>
        </header>

        {/* Overall-Card */}
        {!loading && !error && variants.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="rounded-xl bg-white border border-slate-200 p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Gesamt-Views</p>
              <p className="mt-1 text-3xl font-extrabold text-slate-900" data-testid="admin-landing-total-views">
                {totalViews.toLocaleString("de-DE")}
              </p>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Test-Complete</p>
              <p className="mt-1 text-3xl font-extrabold text-slate-900" data-testid="admin-landing-total-completes">
                {totalCompletes.toLocaleString("de-DE")}
              </p>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-[#4563BB] to-[#3350a8] text-white p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-white/80">Overall-CR</p>
              <p className="mt-1 text-3xl font-extrabold" data-testid="admin-landing-overall-cr">
                {fmtPercent(overallCR)}
              </p>
              <p className="mt-1 text-xs text-white/70">hero_view → test_complete</p>
            </div>
          </div>
        )}

        {loading && <div className="text-center text-slate-500 py-8">Lädt …</div>}
        {error && (
          <div className="rounded-lg bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800" data-testid="admin-landing-error">
            {error}
          </div>
        )}

        {!loading && !error && variants.length === 0 && (
          <div className="rounded-lg bg-slate-100 p-6 text-center text-slate-600">
            Noch keine Landing-Events erfasst. Sobald erste User auf die Startseite kommen, erscheinen hier die Daten.
          </div>
        )}

        {!loading && !error && variants.length > 0 && (
          <div className="space-y-4">
            {variants.map((v) => {
              const isBest = bestVariant && bestVariant.variant === v.variant;
              return (
                <div
                  key={v.variant}
                  className={`rounded-xl border p-5 ${
                    isBest
                      ? "border-emerald-400 bg-emerald-50 shadow-md"
                      : "border-slate-200 bg-white"
                  }`}
                  data-testid={`admin-landing-variant-${v.variant}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-extrabold text-slate-900">
                          {VARIANT_LABELS[v.variant] || `Variante ${v.variant.toUpperCase()}`}
                        </h3>
                        {isBest && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">
                            <Trophy className="w-3 h-3" /> Beste CR
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">Query-Param: <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">?hook={v.variant}</code></p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Conversion</p>
                      <p className="text-2xl font-extrabold text-slate-900" data-testid={`admin-landing-cr-${v.variant}`}>
                        {fmtPercent(v.conversion_rate)}
                      </p>
                    </div>
                  </div>

                  {/* Trichter-Balken */}
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    <FunnelStep label="Hero-View" value={v.hero_view} rate={1} isFirst />
                    <FunnelStep label="CTA-Click" value={v.cta_click} rate={v.cta_rate} />
                    <FunnelStep label="Test-Start" value={v.test_start} rate={v.start_rate} />
                    <FunnelStep label="Complete" value={v.test_complete} rate={v.complete_rate} highlight />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 rounded-lg bg-blue-50 border border-blue-200 p-4">
          <p className="text-xs font-semibold text-blue-900 flex items-center gap-1">
            <TrendingUp className="w-4 h-4" /> Interpretations-Hilfe
          </p>
          <ul className="mt-2 text-xs text-blue-900/80 space-y-1 list-disc pl-5">
            <li><b>Hero-View → CTA-Click</b> zeigt, ob die Headline zieht (schwach = neuer Hook nötig)</li>
            <li><b>CTA-Click → Test-Start</b> zeigt, ob der Button überzeugt (schwach = Button-Copy testen)</li>
            <li><b>Test-Start → Complete</b> zeigt, ob die 5 Fragen zumutbar sind (schwach = zu lang)</li>
            <li><b>Beste CR</b>-Badge nur ab 20+ Views pro Variante (Signifikanz-Schwelle)</li>
          </ul>
        </div>

        {/* Sprint V38 (So 15.09.2026) — Share-Analytics-Kachel */}
        <SourceVariantTable rows={data?.by_source || []} />
        <ShareAnalyticsSection />
      </div>
    </AdminLayout>
  );
}

const SOURCE_LABELS = {
  paid_social: "Paid Social",
  organic_social: "Organisches Social",
  aj_homepage: "AJ-Homepage",
  email: "E-Mail",
  direct: "Direkt / unbekannt",
};

// Quelle x Variante. Daten nur aus der Vorschau (Schritt 1), auf Prod leer und ausgeblendet.
function SourceVariantTable({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6 overflow-x-auto" data-testid="admin-source-variant-table">
      <h2 className="text-xl font-extrabold text-slate-900">Quelle × Variante (Vorschau)</h2>
      <table className="mt-4 w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="py-2 pr-4">Quelle</th><th className="pr-4">Variante</th><th className="pr-4">Hero-Views</th>
            <th className="pr-4">CTA-Klicks</th><th className="pr-4">Test-Starts</th><th>Opt-in-Klicks</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.traffic_source}-${r.variant}`} className="border-t border-slate-100" data-testid={`source-row-${r.traffic_source}-${r.variant}`}>
              <td className="py-2 pr-4">{SOURCE_LABELS[r.traffic_source] || r.traffic_source}</td>
              <td className="pr-4">{VARIANT_LABELS[r.variant] || r.variant}</td>
              <td className="pr-4">{r.hero_view}</td><td className="pr-4">{r.cta_click}</td>
              <td className="pr-4">{r.test_start}</td><td>{r.opt_in_clicked}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ShareAnalyticsSection() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/admin/share-analytics?days=${days}`).then(
      (r) => { if (alive) setData(r.data); },
      (e) => { if (alive) setErr(e?.response?.data?.detail || "load_failed"); },
    );
    return () => { alive = false; };
  }, [days]);
  return (
    <div
      className="mt-8 rounded-2xl bg-white border border-slate-200 p-6"
      data-testid="admin-share-analytics"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Viralität · Sprint V38
          </p>
          <h2 className="mt-1 text-xl font-extrabold text-slate-900">
            Empfehlungs-Marketing (Shares)
          </h2>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          data-testid="share-analytics-period-select"
          className="rounded-lg border border-slate-200 bg-white text-sm px-3 py-2 font-semibold"
        >
          {[7, 14, 30, 90].map((d) => <option key={d} value={d}>{d} Tage</option>)}
        </select>
      </div>
      {err && <p className="text-sm text-red-600" data-testid="share-analytics-error">Konnte Daten nicht laden.</p>}
      {!err && !data && <p className="text-sm text-slate-500">Lädt …</p>}
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="share-analytics-kpis">
            <ShareKpi label="Total Shares" value={data.totals.shares} />
            <ShareKpi label="Mit Referral" value={data.totals.with_referral} />
            <ShareKpi label="Ohne Referral" value={data.totals.without_referral} />
            <ShareKpi label="Referral-Quote" value={`${data.totals.referral_share_pct}%`} highlight />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ShareFacet title="Nach Fokustyp" rows={data.by_fokustyp} testId="share-facet-fokustyp" />
            <ShareFacet title="Nach Kanal" rows={data.by_channel} testId="share-facet-channel" />
            <ShareFacet title="Nach Kontext" rows={data.by_context} testId="share-facet-context" />
            <ShareFacet title="Nach Text-Variante" rows={data.by_variant} testId="share-facet-variant" />
          </div>
          {data.timeline_14d.length > 0 && (
            <div className="mt-6" data-testid="share-timeline">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                Tagesverlauf (letzte 14 Tage)
              </p>
              <div className="flex items-end gap-1 h-24">
                {data.timeline_14d.map((d) => {
                  const max = Math.max(...data.timeline_14d.map((x) => x.count), 1);
                  const h = Math.round((d.count / max) * 100);
                  return (
                    <div
                      key={d.date}
                      className="flex-1 bg-[var(--fts-primary-bright)] rounded-t"
                      style={{ height: `${h}%` }}
                      title={`${d.date}: ${d.count}`}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ShareKpi({ label, value, highlight = false }) {
  return (
    <div className={`rounded-lg p-3 ${highlight ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-0.5 text-xl font-extrabold ${highlight ? "text-amber-700" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}

function ShareFacet({ title, rows, testId }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-lg bg-slate-50 p-4" data-testid={testId}>
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{title}</p>
        <p className="mt-2 text-xs text-slate-400 italic">Keine Daten</p>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="rounded-lg bg-slate-50 p-4" data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">{title}</p>
      <ul className="space-y-1.5">
        {rows.slice(0, 6).map((r) => (
          <li key={String(r.key)} className="flex items-center gap-2 text-sm">
            <span className="w-24 truncate font-semibold text-slate-700 shrink-0">{String(r.key)}</span>
            <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-[var(--fts-primary-bright)]" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
            <span className="text-xs font-semibold text-slate-600 tabular-nums">{r.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FunnelStep({ label, value, rate, isFirst = false, highlight = false }) {
  return (
    <div className={`rounded-lg p-3 ${highlight ? "bg-white border border-emerald-200" : "bg-slate-50"}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 truncate">{label}</p>
      <p className={`mt-0.5 text-xl font-extrabold ${highlight ? "text-emerald-700" : "text-slate-900"}`}>
        {(value || 0).toLocaleString("de-DE")}
      </p>
      {!isFirst && (
        <div className="mt-1 flex items-center gap-1">
          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${pctBarColor(rate)}`}
              style={{ width: `${Math.min(100, (rate || 0) * 100)}%` }}
            />
          </div>
          <span className="text-[10px] font-semibold text-slate-600">{fmtPercent(rate)}</span>
        </div>
      )}
    </div>
  );
}
